import { useMsal } from '@azure/msal-react';
import { FC, FunctionComponent, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { Outlet, RouterProvider, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { store } from './store';
import { useAppDispatch, useAppSelector } from './store/hooks';

import Layout from './components/layout/Layout';

import { appSettingsActions } from './features/common/app-settings/app-settings-slice';
import { selectUserProfile } from './features/common/auth/auth-slice';
import initMsalInstance, { ssoSilent } from './features/common/auth/msal-instance';

import { globalizationLocale } from './features';
import { userProfileActions } from './features/common/auth/auth-slice';
import { CustomNavigationClient } from './utils/NavigationClient';

import { IntlProvider, loadMessages, LocalizationProvider } from '@progress/kendo-react-intl';
import { ExtendedLocalizationService } from './features/common/globalization/extended-localization-service';
import { ExtendedLocalizationContext } from './hooks/use-extended-localization-service';

import enMessages from './assets/messages/en-US.json';
import { ConfirmationServiceProvider, ErrorBoundary } from './components/common';

import createAppRouter, { Router } from './create-app-router';
import { IAppSettings, UserProfile } from './model';
import { useGleap } from './hooks/useGleap';

const LocalizationWrapper: FunctionComponent<{ children?: React.ReactNode }> = ({ children }) => {
  const locale = useAppSelector(globalizationLocale);
  loadMessages(enMessages, locale);

  return (
    <IntlProvider locale={locale}>
      <LocalizationProvider language={locale}>
        <ExtendedLocalizationContext.Provider value={new ExtendedLocalizationService()}>{children}</ExtendedLocalizationContext.Provider>
      </LocalizationProvider>
    </IntlProvider>
  );
};

const App = () => {
  const history = useNavigate();
  const { instance } = useMsal();
  const { initGleap, identifyGleapUser } = useGleap();
  const userProfile = useAppSelector(selectUserProfile);

  useEffect(() => {
    const navigationClient = new CustomNavigationClient(history);
    instance.setNavigationClient(navigationClient);
  }, [history, instance]);

  useEffect(() => {
    initGleap();
  }, [initGleap]);

  useEffect(() => {
    if (userProfile) {
      identifyGleapUser();
    }
  }, [userProfile, identifyGleapUser]);

  return (
    <>
      <ConfirmationServiceProvider>
        <Layout>
          <Outlet />
        </Layout>
      </ConfirmationServiceProvider>

      <ToastContainer autoClose={2000} position='bottom-right' />
    </>
  );
};

const AppWrapper: FC<{ settings: IAppSettings }> = ({ settings }) => {
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const [router, setRouter] = useState<Router | undefined>();

  useEffect(() => {
    dispatch(appSettingsActions.fetchSettings());
  }, [dispatch]);

  useEffect(() => {
    initMsalInstance(instance, (account) => {
      if (account) {
        const userProfile = new UserProfile(account.username, account.name || '', account.idTokenClaims?.roles || []);
        dispatch(userProfileActions.setProfile(userProfile));
      }
    })
      .then((msal) => ssoSilent(msal).then((res) => msal))
      .then((msal) => {
        setRouter(createAppRouter(<App />));
        return msal;
      });
  }, [dispatch, instance, settings]);

  if (!router) {
    return <div></div>;
  }

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <LocalizationWrapper>
          <RouterProvider router={router} />
        </LocalizationWrapper>
      </Provider>
    </ErrorBoundary>
  );
};

export default AppWrapper;
